import type { GanttData, Row, Task } from './types';
import { parseDate } from './dateUtils';
import { genId } from './idGen';

type TaskWithMeta = {
  task: Task;
  incoming: number;
  originalIndex: number;
  startMs: number;
  endMs: number;
};

export const autoArrange = (state: GanttData): GanttData => {
  const incomingCount = new Map<string, number>();
  state.tasks.forEach(t => incomingCount.set(t.id, 0));

  state.dependencies.forEach(d => {
    if (!incomingCount.has(d.toTaskId)) return;
    incomingCount.set(d.toTaskId, (incomingCount.get(d.toTaskId) ?? 0) + 1);
  });

  const tasksMeta: TaskWithMeta[] = state.tasks.map((t, idx) => ({
    task: t,
    incoming: incomingCount.get(t.id) ?? 0,
    originalIndex: idx,
    startMs: parseDate(t.startDate).getTime(),
    endMs: parseDate(t.endDate).getTime(),
  }));

  tasksMeta.sort((a, b) => {
    if (a.task.startDate !== b.task.startDate) return a.task.startDate < b.task.startDate ? -1 : 1;
    if (a.incoming !== b.incoming) return a.incoming - b.incoming;
    return a.originalIndex - b.originalIndex;
  });

  // Greedy first-fit packing into rows: put task in first row where start >= rowEnd
  const bins: { id: string; endMs: number; taskIds: string[] }[] = [];

  tasksMeta.forEach(m => {
    const i = bins.findIndex(r => m.startMs >= r.endMs);
    if (i >= 0) {
      bins[i].taskIds.push(m.task.id);
      bins[i].endMs = Math.max(bins[i].endMs, m.endMs);
    } else {
      bins.push({
        id: genId('row'),
        endMs: m.endMs,
        taskIds: [m.task.id],
      });
    }
  });

  const rows: Row[] = bins.map(b => ({ id: b.id, taskIds: b.taskIds }));

  return {
    ...state,
    tasks: tasksMeta.map(m => m.task),
    rows,
  };
};
